import { type CurrencyCode, feCommonDataHolder } from '@lastbrand/fe-common'

import { getCurrencyFromContext } from '@src/blocks/currency/currency.hook'
import { FALLBACK_LOCALE, LOCALE_COOKIE } from '@src/config/localisation/locale-config.constants'
import regionDetailsHolder from '@src/quince-site-common/src/data-holders/regionDetails.holder'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

/**
 * Region details for currency resolution when React locale context is not available
 * (initializeFeCommon runs at module load, before providers).
 */
export function getRegionDetailsForCurrencyInit(): Parameters<typeof getCurrencyFromContext>[1] {
  // regionDetailsHolder is populated by the React provider tree and works in
  // both SSR and browser contexts. localStorage is browser-only, so falls back
  // to the stored localisation data only when running in the browser.
  const fromHolder = regionDetailsHolder.getRegionDetails()
  if (fromHolder) {
    return fromHolder
  }

  if (isBrowser()) {
    const stored = getLocalisationDataFromStorage()
    if (stored) {
      const country = stored.country.toLowerCase()
      return {
        regions: {
          [country]: { defaultCurrencyIsoCode: stored.currency },
        },
      }
    }
  }

  return undefined
}

export const initializeFeCommon = () => {
  const locale = getCookie(LOCALE_COOKIE) ?? FALLBACK_LOCALE
  const currency: CurrencyCode = getCurrencyFromContext(locale, getRegionDetailsForCurrencyInit())
  const env = process.env.NEXT_PUBLIC_FE_COMMON_ENV ?? 'PROD'

  ;(
    feCommonDataHolder as {
      initialize: (options: {
        env: string
        flags: { enableI18n: boolean }
        locale: string
        currency: CurrencyCode
      }) => void
    }
  ).initialize({
    env,
    flags: {
      enableI18n: shouldEnableI18nClient(),
    },
    locale,
    currency,
  })
}
